import React, { useContext } from 'react';
import { IModifiersContext, ModifiersContext } from '../../../modifiersState/modifiersContext';
import { UserRoles, IModifiersByCustomerV2 } from 'types';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { Link } from 'react-router-dom';
import { useLoggedUserRestaurants } from 'hooks/useLoggedUserRestaurants';
import Text from 'components/Text/Text';
import { ImgUploaderByPreSignedUrl } from 'components/ImgUploader/ImgUploaderByPreSignedUrl';
import { RestaurantsAsLinkList } from 'components/Lists/ListView/RestaurantsAsLinkList';
import DataController from 'components/DataController/DataController';
import classNames from 'classnames';
import { PricesListV2 } from 'components/Lists/ListView/PricesListV2';
import style from './Item.module.scss';

interface IProps {
	data: IModifiersByCustomerV2['items'][0];
	index: number;
}

export const Item: React.FC<IProps> = ({ data, index }) => {
	const { setIDForDeletion, setIDForAssignRestaurants, setIDForEditModGroupTemplate } =
		useContext<IModifiersContext>(ModifiersContext);
	const hasEditAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.RESTAURANT_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const modifierRowClasses = classNames(style.modifier, style.row);
	const modifierNameClasses = classNames(style.modifierName, style.row);
	const priceRowClasses = classNames(style.price, style.row);
	const articleNumberRowClasses = classNames(style.articleNumber, style.row);
	const restaurantsRowClasses = classNames(style.restaurants, style.row);
	const actionRowClasses = classNames(style.action, style.row);

	const restaurants = data.restaurants.map((restaurant) => ({
		...restaurant,
		restaurantName: restaurant.name,
	}));
	const accessibleRestaurants = useLoggedUserRestaurants(restaurants);

	const editButtonElement = accessibleRestaurants.length ? (
		<div className={style.editItemWrapper}>
			<Link to={`/menu-v2/modifiers/edit?modifierID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editMainInfo" size="s" />
			</Link>
			<div className={style.linkStyle} onClick={() => setIDForAssignRestaurants(data.id)}>
				<Text translationNameSpace="listing" translationKey="editRestaurantsList" size="s" />
			</div>
			<Link to={`/menu-v2/modifiers/edit-restaurant-properties?modifierID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editRestaurantsProperties" size="s" />
			</Link>
			<div className={style.linkStyle} onClick={() => setIDForEditModGroupTemplate(data.id)}>
				<Text translationNameSpace="listing" translationKey="editNestedModGroupsTemplate" size="s" />
			</div>
		</div>
	) : (
		<div className={style.editItemWrapper}>
			<Link to={`/menu-v2/modifiers/edit?modifierID=${data.id}`}>
				<Text translationNameSpace="listing" translationKey="editMainInfo" size="s" />
			</Link>
			<div className={style.linkStyle} onClick={() => setIDForAssignRestaurants(data.id)}>
				<Text translationNameSpace="listing" translationKey="assignRestaurants" size="s" />
			</div>
		</div>
	);

	return (
		<div data-test={`Item-${index}`}>
			<div className={style.container}>
				<div className={modifierRowClasses}>
					<div className={style.imgWrapper}>
						<ImgUploaderByPreSignedUrl loaderSize="small" preSignedUrl={data.preSignedUrl} />
					</div>
					<Text
						className={modifierNameClasses}
						translationKey={data.name}
						withTooltip={true}
						data-test="modifierName"
					/>
				</div>
				<div className={restaurantsRowClasses}>
					<RestaurantsAsLinkList restaurants={accessibleRestaurants} />
				</div>
				<div className={priceRowClasses}>
					<PricesListV2 restaurants={restaurants} />
				</div>
				<Text
					className={articleNumberRowClasses}
					translationNameSpace="form"
					translationKey={data.articleNumber}
					withTooltip={true}
					data-test="articleNumber"
				/>
				<div className={actionRowClasses}>
					<DataController
						URLWithIDToInfo={`/menu-v2/modifiers/view?modifierID=${data.id}`}
						customEditButton={hasEditAccess && editButtonElement}
						onClickDelete={() => setIDForDeletion(data.id)}
						size="smallBtn"
					/>
				</div>
			</div>
		</div>
	);
};
